import mqtt, { IClientOptions, MqttClient } from 'mqtt';
import { V1_CHANNEL_TOPIC, V1Channel } from './v1Transforms';
import { keepLongCodeExact } from './v1Errors';
import { V1PublishKind } from './v1Commands';

/**
 * One browser connection to the legacy Mosquitto broker, scoped to ONE V1 / Coldfire spa.
 *
 * Mirrors the mobile app's `V1Transport` + `V1Connection` (which run over TCP) on the
 * broker's WebSocket listener, which the legacy ASP.NET portal used from browsers.
 *
 * Why per spa: the broker's auth plugin ties the JWT's `sub` to the spa GUID in the
 * topic path (`arctic/spa/{guid}/...`), so a token, and therefore a socket, serves one
 * spa. The dealer portal's AWS IoT client (`services/mqtt/mqtt.ts`) is untouched; this
 * is a separate client that only exists while a V1 spa's control pages are open.
 *
 * Auth: username = the spa-scoped JWT the backend mints (`/spas/{guid}/v1-mqtt-token`),
 * password = the literal "anything", like the legacy apps. The broker reads the token
 * at CONNECT only, and its plugin refuses silently once the token has expired while
 * the socket stays open — so the session is moved onto a fresh token before then.
 *
 * GUID casing: the legacy DB stores many GUIDs uppercase while the legacy server
 * publishes with C#'s lowercase `Guid.ToString()`, and MQTT topics are case-sensitive.
 * The ACL lowercases both sides, so subscribing and publishing under every casing we
 * know works whichever one the other end uses (a lowercase id stays one topic).
 */

export type V1ClientStatus = 'idle' | 'connecting' | 'connected' | 'disconnected' | 'error';

export interface V1TokenInfo {
  token: string;
  /** ms since the epoch */
  expiresAt: number;
  brokerUrl: string;
  /** The GUID as the legacy DB stores it. */
  spaGuid: string;
}

export interface V1SpaClientOptions {
  /** Mint (or re-mint) a spa-scoped token. Called lazily and again before expiry. */
  getToken: () => Promise<V1TokenInfo>;
  onMessage: (channel: V1Channel, payload: any, topic: string) => void;
  onStatus?: (status: V1ClientStatus, detail?: string) => void;
  /** `source.name` stamped on every publish. */
  sourceName?: string;
}

// Wrapper key for each publish kind on the single controls/spa topic
// (MqttManager.MQTT_PUBLISH_TOPIC._wrapper in the legacy Android app).
const PUBLISH_WRAPPER: Record<V1PublishKind, string> = {
  command: 'spaCommand',
  settings: 'spaSettings',
  spaboySettings: 'spaboySettings',
  peak: 'peak',
};

const TOPIC_PREFIX = 'arctic/spa/';
const CONTROLS_SUFFIX = 'controls/spa';

// Reconnect backoff after a drop: quick first retry, then growing steps, capped.
const RECONNECT_DELAY_INITIAL_MS = 1000;
const RECONNECT_DELAY_STEP_MS = 1500;
const RECONNECT_DELAY_MAX_MS = 120000;

// Renew the token this far before it expires; never sooner than this after connecting
// so a token already inside the margin (or a skewed clock) cannot loop.
const TOKEN_RENEW_MARGIN_MS = 2 * 60 * 1000;
const TOKEN_RENEW_MIN_DELAY_MS = 5 * 1000;
// Reuse a cached token while it has at least this long left.
const TOKEN_REUSE_MARGIN_MS = 60 * 1000;

const CONNECT_TIMEOUT_MS = 15000;

const hex = (n: number) => {
  let s = '';
  while (s.length < n) s += Math.floor(Math.random() * 16).toString(16);
  return s.slice(0, n);
};

export class V1SpaClient {
  readonly spaId: string;
  private readonly opts: V1SpaClientOptions;
  private client: MqttClient | null = null;
  private status: V1ClientStatus = 'idle';
  private started = false;
  private connecting: Promise<void> | null = null;
  private cachedToken: V1TokenInfo | null = null;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private reconnectDelay = RECONNECT_DELAY_INITIAL_MS;
  private renewTimer: ReturnType<typeof setTimeout> | null = null;
  private renewing = false;
  /** Set when a subscribe was refused and a fresh token fetched for it; cleared on success. */
  private refusedWithFreshToken = false;
  private firstMessageTopics = new Set<string>();

  constructor(spaId: string, opts: V1SpaClientOptions) {
    this.spaId = spaId;
    this.opts = opts;
  }

  private tag() {
    return `[V1 ${this.spaId.slice(0, 8)}]`;
  }

  /** Every casing of the GUID this spa's topics may use. */
  private casings(): string[] {
    const ids = new Set<string>();
    ids.add(this.spaId);
    if (this.cachedToken?.spaGuid) ids.add(this.cachedToken.spaGuid);
    ids.add(this.spaId.toLowerCase());
    return Array.from(ids);
  }

  private topics(suffix: string): string[] {
    return this.casings().map((id) => `${TOPIC_PREFIX}${id}/${suffix}`);
  }

  getStatus(): V1ClientStatus {
    return this.status;
  }

  isConnected(): boolean {
    return !!this.client?.connected;
  }

  private setStatus(status: V1ClientStatus, detail?: string) {
    this.status = status;
    try {
      this.opts.onStatus?.(status, detail);
    } catch (err) {
      console.warn(`${this.tag()} status listener threw:`, err);
    }
  }

  /** Open the socket (idempotent). Safe to call again after a drop. */
  start(): Promise<void> {
    this.started = true;
    if (!this.resumeListening && typeof document !== 'undefined') {
      this.resumeListening = true;
      document.addEventListener('visibilitychange', this.onResume);
      window.addEventListener('online', this.onResume);
    }
    return this.ensureConnected();
  }

  /** Close the socket for good and stop every timer. */
  stop(): void {
    this.started = false;
    if (this.resumeListening && typeof document !== 'undefined') {
      this.resumeListening = false;
      document.removeEventListener('visibilitychange', this.onResume);
      window.removeEventListener('online', this.onResume);
    }
    this.clearReconnectTimer();
    this.clearRenewTimer();
    this.teardownClient(true);
    this.setStatus('idle');
  }

  private ensureConnected(): Promise<void> {
    if (this.client?.connected) return Promise.resolve();
    if (this.connecting) return this.connecting;
    const attempt = this.connect().finally(() => {
      if (this.connecting === attempt) this.connecting = null;
    });
    this.connecting = attempt;
    return attempt;
  }

  private async fetchToken(fresh = false): Promise<V1TokenInfo> {
    if (!fresh && this.cachedToken && this.cachedToken.expiresAt - TOKEN_REUSE_MARGIN_MS > Date.now()) {
      return this.cachedToken;
    }
    const info = await this.opts.getToken();
    this.cachedToken = info;
    return info;
  }

  private async connect(fresh = false): Promise<void> {
    if (!this.started) return;
    this.setStatus('connecting');
    let token: V1TokenInfo;
    try {
      token = await this.fetchToken(fresh);
    } catch (err: any) {
      // A token the backend will not mint (no legacy owner, auth service down) is not
      // something a retry fixes quickly; back off like a dropped socket would.
      console.warn(`${this.tag()} token fetch failed:`, err?.response?.data?.data ?? err?.message ?? err);
      this.setStatus('error', 'token');
      this.scheduleReconnect();
      return;
    }
    if (!this.started) return;
    if (!token.brokerUrl) {
      console.warn(`${this.tag()} no V1 broker URL configured for this environment`);
      this.setStatus('error', 'broker-url');
      return;
    }

    this.teardownClient(true);

    const options: IClientOptions = {
      // Mosquitto often enforces the MQTT 3.1 23-char clientId limit; mirror Paho's short hex id.
      clientId: `arc${hex(20)}`,
      username: token.token,
      password: 'anything',
      protocolVersion: 4,
      clean: true,
      keepalive: 60,
      connectTimeout: CONNECT_TIMEOUT_MS,
      // Reconnects are handled here so each one can carry a fresh token.
      reconnectPeriod: 0,
    };

    const client = mqtt.connect(token.brokerUrl, options);
    this.client = client;

    client.on('connect', () => {
      if (this.client !== client) return;
      this.reconnectDelay = RECONNECT_DELAY_INITIAL_MS;
      this.clearReconnectTimer();
      console.log(`${this.tag()} connected — ${token.brokerUrl}`);
      this.subscribeAll(client);
      this.setStatus('connected');
      this.scheduleRenewal();
    });

    client.on('message', (topic, message) => {
      if (this.client !== client) return;
      this.handleMessage(topic, message);
    });

    client.on('error', (err) => {
      if (this.client !== client) return;
      console.warn(`${this.tag()} client error:`, err?.message ?? err);
      this.setStatus('error', err?.message);
    });

    client.on('close', () => {
      if (this.client !== client) return;
      console.log(`${this.tag()} disconnected`);
      this.clearRenewTimer();
      this.setStatus('disconnected');
      this.scheduleReconnect();
    });
  }

  private subscribeAll(client: MqttClient) {
    const topics: string[] = [];
    (Object.keys(V1_CHANNEL_TOPIC) as V1Channel[]).forEach((channel) => {
      topics.push(...this.topics(V1_CHANNEL_TOPIC[channel]));
    });
    client.subscribe(topics, { qos: 0 }, (err, granted) => {
      if (err) {
        console.error(`${this.tag()} subscribe failed:`, err);
        return;
      }
      // The broker's ACL answers a refused topic with qos 128 rather than an error.
      const refused = (granted ?? []).filter((g) => g.qos === 128).map((g) => g.topic);
      if (refused.length === 0) {
        this.refusedWithFreshToken = false;
        console.log(`${this.tag()} subscribed to ${topics.length} topic(s)`);
        return;
      }
      if (this.refusedWithFreshToken) {
        // A token minted moments ago is refused too: not expiry but the ACL itself
        // (wrong spa, or the broker's auth backend). Re-minting in a loop won't help.
        console.error(`${this.tag()} subscriptions refused again on a fresh token; giving up until the next reconnect:`, refused);
        return;
      }
      console.warn(`${this.tag()} subscriptions refused (token expired?), renewing it:`, refused);
      this.refusedWithFreshToken = true;
      this.renewSession();
    });
  }

  private channelFor(topic: string): V1Channel | null {
    if (!topic.startsWith(TOPIC_PREFIX)) return null;
    const rest = topic.slice(TOPIC_PREFIX.length);
    const slash = rest.indexOf('/');
    if (slash < 0) return null;
    const id = rest.slice(0, slash);
    if (!this.casings().some((c) => c.toLowerCase() === id.toLowerCase())) return null;
    const suffix = rest.slice(slash + 1);
    const entry = (Object.keys(V1_CHANNEL_TOPIC) as V1Channel[]).find((ch) => V1_CHANNEL_TOPIC[ch] === suffix);
    return entry ?? null;
  }

  private handleMessage(topic: string, message: Buffer) {
    const channel = this.channelFor(topic);
    if (!channel) return;
    let payload: any;
    try {
      const text = message.toString();
      payload = text.trim() ? JSON.parse(keepLongCodeExact(text)) : null;
    } catch (err) {
      console.warn(`${this.tag()} non-JSON message on ${topic}`);
      return;
    }
    if (!this.firstMessageTopics.has(topic)) {
      this.firstMessageTopics.add(topic);
      console.log(`${this.tag()} first message on ${topic}`);
    }
    try {
      this.opts.onMessage(channel, payload, topic);
    } catch (err) {
      console.error(`${this.tag()} message handler threw:`, err);
    }
  }

  /**
   * Publish one envelope to controls/spa. The other end subscribes under one casing
   * only, so writing to every casing still reaches it exactly once.
   * @returns whether the socket accepted the publish
   */
  publish(kind: V1PublishKind, body: Record<string, any>): boolean {
    const wrapper = PUBLISH_WRAPPER[kind];
    if (!wrapper) {
      console.warn(`${this.tag()} unknown publish kind "${kind}"`);
      return false;
    }
    if (!body || typeof body !== 'object' || Object.keys(body).length === 0) {
      console.warn(`${this.tag()} ${kind}: empty payload, ignored`);
      return false;
    }
    if (!this.client?.connected) {
      console.warn(`${this.tag()} publish dropped — not connected`, { kind });
      // Lazy reconnect; the caller can retry once the socket is back.
      if (this.started) this.ensureConnected();
      return false;
    }
    const payload = JSON.stringify({
      [wrapper]: {
        ...body,
        source: { name: this.opts.sourceName ?? 'Web' },
      },
    });
    let ok = false;
    this.topics(CONTROLS_SUFFIX).forEach((topic) => {
      try {
        this.client!.publish(topic, payload, { qos: 0, retain: false });
        ok = true;
      } catch (err) {
        console.warn(`${this.tag()} publish failed:`, err);
      }
    });
    if (ok) console.log(`${this.tag()} published ${kind}`, Object.keys(body).join(','));
    return ok;
  }

  // ── token renewal ────────────────────────────────────────────────────────────

  private clearRenewTimer() {
    if (this.renewTimer) {
      clearTimeout(this.renewTimer);
      this.renewTimer = null;
    }
  }

  private scheduleRenewal() {
    this.clearRenewTimer();
    const expiresAt = this.cachedToken?.expiresAt;
    if (!expiresAt) return;
    const delay = Math.max(TOKEN_RENEW_MIN_DELAY_MS, expiresAt - TOKEN_RENEW_MARGIN_MS - Date.now());
    this.renewTimer = setTimeout(() => {
      this.renewTimer = null;
      this.renewSession();
    }, delay);
  }

  /** Fetch a fresh token and move the socket onto it (silently, so no backoff races it). */
  private async renewSession() {
    if (!this.started || this.renewing) return;
    this.renewing = true;
    try {
      this.clearReconnectTimer();
      await this.connect(true);
      console.log(`${this.tag()} renewed the spa token and reopened the socket`);
    } catch (err: any) {
      console.warn(`${this.tag()} token renewal failed:`, err?.message ?? err);
      this.scheduleReconnect();
    } finally {
      this.renewing = false;
    }
  }

  // ── reconnect ───────────────────────────────────────────────────────────────

  private clearReconnectTimer() {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }

  private resumeListening = false;

  /**
   * A drop while the tab was hidden (or offline) should not wait out the backoff once the
   * user is looking again: reconnect right away, as the mobile app does on foreground.
   */
  private onResume = () => {
    if (!this.started || this.client?.connected) return;
    if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
    this.clearReconnectTimer();
    this.reconnectDelay = RECONNECT_DELAY_INITIAL_MS;
    this.ensureConnected();
  };

  private scheduleReconnect() {
    if (!this.started || this.reconnectTimer) return;
    const delay = this.reconnectDelay;
    this.reconnectDelay = Math.min(delay + RECONNECT_DELAY_STEP_MS, RECONNECT_DELAY_MAX_MS);
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      if (!this.started || this.client?.connected) return;
      this.ensureConnected();
    }, delay);
  }

  /** Close the current socket without reporting a drop (so no reconnect is scheduled for it). */
  private teardownClient(silent: boolean) {
    const client = this.client;
    if (!client) return;
    this.client = null;
    try {
      client.removeAllListeners();
      if (silent) client.on('error', () => undefined);
      client.end(true);
    } catch (err) {
      console.warn(`${this.tag()} error closing socket:`, err);
    }
  }
}

export default V1SpaClient;
